import { Link } from '@mui/material';
import type { AnchorHTMLAttributes, ComponentProps } from 'react';
import { memo, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import type { Citation } from '../../state/chatTypes';
import { buildCitationUrlByIndex } from './citations';
import useStyles from './MessageMarkdown.styles';
import { createRemarkCitationLinks } from './remarkCitationLinks';
import { sanitizeSchema } from './sanitizeSchema';

type RemarkPluginList = NonNullable<
  ComponentProps<typeof ReactMarkdown>['remarkPlugins']
>;
type RehypePluginList = NonNullable<
  ComponentProps<typeof ReactMarkdown>['rehypePlugins']
>;

export type MessageMarkdownProps = {
  /** Raw markdown source from the assistant message. */
  content: string;
  /** Optional className appended to the root element. */
  className?: string;
  /**
   * Optional citations used to linkify inline `[N]` markers in `content`.
   * Markers whose citation lacks an HTTPS URL are left as plain text.
   */
  citations?: ReadonlyArray<Citation>;
};

const BASE_REMARK_PLUGINS: RemarkPluginList = [
  [remarkGfm, { singleTilde: false }],
];
const rehypePlugins: RehypePluginList = [[rehypeSanitize, sanitizeSchema]];

const components: Components = {
  a: ({
    node: _node,
    href,
    children,
    ...rest
  }: AnchorHTMLAttributes<HTMLAnchorElement> & {
    node?: unknown;
  }) => (
    <Link
      href={href}
      underline="always"
      color="inherit"
      {...rest}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </Link>
  ),
};

/** Renders sanitized GFM markdown for assistant messages. */
function MessageMarkdownInner({
  content,
  className,
  citations,
}: MessageMarkdownProps) {
  const { classes, cx } = useStyles();

  const remarkPlugins = useMemo<RemarkPluginList>(() => {
    const citationUrlByIndex = buildCitationUrlByIndex(citations);
    if (citationUrlByIndex.size === 0) {
      return BASE_REMARK_PLUGINS;
    }
    return [
      ...BASE_REMARK_PLUGINS,
      createRemarkCitationLinks(citationUrlByIndex),
    ];
  }, [citations]);

  return (
    <div
      className={cx(classes.root, className)}
      data-testid="ai-sidekick-message-markdown"
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

const MessageMarkdown = memo(MessageMarkdownInner);
MessageMarkdown.displayName = 'MessageMarkdown';

export default MessageMarkdown;
